import { Button, Input, Menu, MenuButton, MenuPopover, MenuTrigger, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Title1, Tooltip, mergeClasses  } from '@fluentui/react-components';
import { AddRegular, ArrowSortRegular, CheckmarkRegular, DeleteRegular, DismissRegular, EditRegular, FilterRegular, Settings48Regular } from '@fluentui/react-icons';
import { Dropdown, Option } from '@fluentui/react-components';
import { FC, useEffect, useMemo, useState } from 'react';
import { ClientConfiguration, UpdateUsersRoleRequest, UserRoleAssignment } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useFeatures } from '../../store/hooks';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import { useIsMobile } from '../../Hooks/useResponsive';

// Props interface for UserManagementView
interface UserManagementViewProps {
    classes: Record<string, string>;
    t: (key: string, defaultValue?: string) => string;

}

const UserManagementView: FC<UserManagementViewProps> = (props) => {

    const isMobile = useIsMobile();

    const {
        classes,
        t,
    } = props;

    const { features } = useFeatures();
    const clientConfiguration = features.chat as ClientConfiguration; // features.chat contain in fact ClientConfiguration
    
    // State to store user data retrieved from API
    const [usersData, setUsersData] = useState<{ userId: string; role: string }[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);

    // State for sorting and filtering
    const [sortConfig, setSortConfig] = useState<{ key: 'userId' | 'role'; direction: 'asc' | 'desc' } | null>(null);
    const [filters, setFilters] = useState({ userId: '', role: '' });

    // State for editing
    const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
    const [editingData, setEditingData] = useState<{ userId: string; role: string }>({ userId: '', role: '' });
    const [isAddingNew, setIsAddingNew] = useState(false);


    const [showRoleConfig, setShowRoleConfig] = useState(false);
    const [roleConfigData, setRoleConfigData] = useState('');
    const [roleConfigLoading, setRoleConfigLoading] = useState(false);
    const [roleConfigError, setRoleConfigError] = useState<string | null>(null);
    const [selectedRoleForConfig, setSelectedRoleForConfig] = useState<string>('');
    
    // Function to fetch and show role configuration
    const handleShowRoleConfig = async (role: string) => {
        setShowRoleConfig(true);
        setRoleConfigLoading(true);
        setRoleConfigError(null);
        setSelectedRoleForConfig(role);
        try {
            // getRoleConfiguration is assumed to be available via performEnterpriseGPTApiCall
            const config = await performEnterpriseGPTApiCall(
                (client) => client.getRoleConfiguration(role)
            );            
            setRoleConfigData(typeof config === 'string' ? config : JSON.stringify(config, null, 2));
        } catch (err) {
            setRoleConfigError(t('adminSettings.rbac.error.roleConfigFailed', 'Failed to load role configuration'));
            setRoleConfigData('');
        } finally {
            setRoleConfigLoading(false);
        }
    };

    // Function to close/hide the role config section
    const handleCloseRoleConfig = () => {
        setShowRoleConfig(false);
        setRoleConfigData('');
        setRoleConfigError(null);
        setSelectedRoleForConfig('');
    };

    // Load user data from API
    useEffect(() => {
        const loadUserRoleAssignments = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                const result = await performEnterpriseGPTApiCall(
                    (client) => client.getAllUserRoleAssignments()
                );

                if (result) {
                    // Check if result is an array or a single object
                    let transformedData: { userId: string; role: string }[] = [];
                    
                    if (Array.isArray(result)) {
                        // If it's an array of UserRoleAssignment objects
                        transformedData = result.map((item: UserRoleAssignment) => ({
                            userId: item.userId || 'N/A',
                            role: item.role || 'N/A'
                        }));
                    } else {
                        // If it's a single UserRoleAssignment object
                        const userRoleAssignment = result as UserRoleAssignment;
                        transformedData = [{
                            userId: userRoleAssignment.userId || 'N/A',
                            role: userRoleAssignment.role || 'N/A'
                        }];
                    }
                    
                    setUsersData(transformedData);
                } else {
                    setUsersData([]);
                }
            } catch (err) {
                console.error('Error loading role assignments:', err);
                setError('Error loading data');
                // Fallback with example data in case of error
                setUsersData([
                    { userId: 'Not found', role: 'Not found' }
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        const loadAvailableRoles = async () => {
            try {
                const roles = await performEnterpriseGPTApiCall(
                    (client) => client.getAllRoleNames()
                );
                setAvailableRoles(Array.isArray(roles) ? roles : []);
            } catch {
                setAvailableRoles([]);
            }
        };

        // Only load data if user is admin
        if (clientConfiguration?.isAdmin === true) {
            loadUserRoleAssignments();
            loadAvailableRoles();
        } else {
            setIsLoading(false);
        }
    }, [clientConfiguration?.isAdmin]);

    // Sorting and filtering functions
    const handleSort = (key: 'userId' | 'role') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleFilterChange = (key: 'userId' | 'role', value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Editing functions
    const handleAddNew = () => {
        setIsAddingNew(true);
        setEditingData({ userId: '', role: '' });
        // Insert new row at the end
        setUsersData(prev => [...prev, { userId: '', role: '' }]);
        setEditingRowIndex(usersData.length);
    };

    const handleEdit = (originalData: { userId: string; role: string }) => {
        // Find the original index in usersData
        const originalIndex = usersData.findIndex(user => 
            user.userId === originalData.userId && user.role === originalData.role
        );
        setEditingRowIndex(originalIndex);
        setEditingData({ ...originalData });
        setIsAddingNew(false);
    };

    const handleCancel = () => {
        if (isAddingNew) {
            // Remove the new empty row
            setUsersData(prev => prev.slice(0, -1));
            setIsAddingNew(false);
        }
        setEditingRowIndex(null);
        setEditingData({ userId: '', role: '' });
    };

    const handleSave = async () => {
        try {
            if (editingData.userId.trim() === '' || editingData.role.trim() === '') {
                setError(t('adminSettings.rbac.validation.emptyFields', 'Please fill in all fields'));
                return;
            }

            // Call the updateUsersRole API
            const updateRequest = new UpdateUsersRoleRequest({
                users: [editingData.userId],
                role: editingData.role
            });

            await performEnterpriseGPTApiCall(
                (client) => client.updateUsersRole(updateRequest)
            );

            // Update the local data
            if (isAddingNew) {
                setUsersData(prev => {
                    const newData = [...prev];
                    newData[newData.length - 1] = { ...editingData };
                    return newData;
                });
                setIsAddingNew(false);
            } else {
                setUsersData(prev => {
                    const newData = [...prev];
                    newData[editingRowIndex!] = { ...editingData };
                    return newData;
                });
            }

            setEditingRowIndex(null);
            setEditingData({ userId: '', role: '' });
            setError(null);
        } catch (err) {
            console.error('Error updating user role:', err);
            setError(t('adminSettings.rbac.error.updateFailed', 'Failed to update user role'));
        }
    };

    const handleInputChange = (field: 'userId' | 'role', value: string) => {
        setEditingData(prev => ({ ...prev, [field]: value }));
    };

    const handleDelete = async (userData: { userId: string; role: string }) => {
        // Show confirmation dialog
        const confirmed = window.confirm(
            t('adminSettings.rbac.deleteConfirmation', 
              `Are you sure you want to delete the user/role assignment for "${userData.userId}" with role "${userData.role}"?`
            )
        );

        if (!confirmed) {
            return;
        }

        try {
            // Call the deleteUserData API
            await performEnterpriseGPTApiCall(
                (client) => client.deleteUserData(userData.userId)
            );

            // Remove the item from local data
            setUsersData(prev => prev.filter(user => 
                !(user.userId === userData.userId && user.role === userData.role)
            ));

            setError(null);
        } catch (err) {
            console.error('Error deleting user data:', err);
            setError(t('adminSettings.rbac.error.deleteFailed', 'Failed to delete user data'));
        }
    };

    // Filtered and sorted data
    const filteredAndSortedData = useMemo(() => {
        const filteredData = usersData.filter(user =>
            user.userId.toLowerCase().includes(filters.userId.toLowerCase()) &&
            user.role.toLowerCase().includes(filters.role.toLowerCase())
        );

        if (sortConfig) {
            filteredData.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    }, [usersData, filters, sortConfig]);

    if (!clientConfiguration?.isAdmin === true)
        return ( // If the user is not an admin, do not render the component
            <div className={classes.container}>
                <div className={classes.titleContainer}>
                    <Settings48Regular className={classes.settingsIcon} />
                    <Title1 className="settingsTitle">{t('adminSettings.forbiddenPageAccess', 'no Access')}</Title1>
                </div>
            </div>
        );

    return (
        <div>
            <div className="titleSection">
                <div className="titleSection-content titleSection-content-indented">
                    <Title1 className="settingsTitle">
                        {t('adminSettings.rbac.title', 'User Management')}
                    </Title1>
                </div>
            </div>

            <HorizontalSeparatorLine />

            <div className={classes.container}>            
                <div className={classes.settingsContent}>
                    {/* RBAC Settings Section */}
                    <div className={classes.settingsSection}>
    
                        {/* User and roles table */}
                        <div>
                            {isLoading ? (
                                <div className="settings-skeleton-center">
                                    <div className="react-loading-skeleton settings-skeleton-card h-460 w-100p mb-16" />
                                </div>
                            ) : error ? (
                                <Text style={{ color: 'red' }}>
                                    {t('adminSettings.rbac.error', 'Error:')} {error}
                                </Text>
                            ) : (
                                <>
                                    {/* Error display */}
                                    {error && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <Text style={{ color: 'red' }}>
                                                {error}
                                            </Text>
                                        </div>
                                    )}
                                    
                                    {/* Filtering information */}
                                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Text size={300}>
                                            {t('adminSettings.rbac.resultsCount', 'Results:')} {filteredAndSortedData.length} / {usersData.length}
                                        </Text>
                                        {(filters.userId || filters.role) && (
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => setFilters({ userId: '', role: '' })}
                                            >
                                                {t('adminSettings.rbac.clearFilters', 'Clear filters')}
                                            </Button>
                                        )}
                                    </div>
                                <Table arial-label="User/Role Assignments Table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell style={{ fontWeight: 'bold' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <Text>{t('adminSettings.rbac.userId', 'User')}</Text>
                                                        <Button
                                                            appearance="subtle"
                                                            size="small"
                                                            icon={<ArrowSortRegular />}
                                                            onClick={() => handleSort('userId')}
                                                            title={t('adminSettings.rbac.sortUserId', 'Sort by User')}
                                                        />
                                                        <Menu>
                                                            <MenuTrigger disableButtonEnhancement>
                                                                <MenuButton
                                                                    appearance="subtle"
                                                                    size="small"
                                                                    icon={<FilterRegular />}
                                                                    title={t('adminSettings.rbac.filterUserId', 'Filter User')}
                                                                />
                                                            </MenuTrigger>
                                                            <MenuPopover>
                                                                <div style={{ padding: '8px' }}>
                                                                    <Input
                                                                        placeholder={t('adminSettings.rbac.filterUserIdPlaceholder', 'Filter by User...')}
                                                                        value={filters.userId}
                                                                        onChange={(e) => handleFilterChange('userId', e.target.value)}
                                                                        size="small"
                                                                    />
                                                                </div>
                                                            </MenuPopover>
                                                        </Menu>
                                                    </div>
                                                    {sortConfig?.key === 'userId' && (
                                                        <Text size={200} style={{ color: '#0078d4' }}>
                                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                        </Text>
                                                    )}
                                                </div>
                                            </TableHeaderCell>
                                            <TableHeaderCell style={{ fontWeight: 'bold' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <Text>{t('adminSettings.rbac.role', 'Role')}</Text>
                                                        <Button
                                                            appearance="subtle"
                                                            size="small"
                                                            icon={<ArrowSortRegular />}
                                                            onClick={() => handleSort('role')}
                                                            title={t('adminSettings.rbac.sortRole', 'Sort by Role')}
                                                        />
                                                        <Menu>
                                                            <MenuTrigger disableButtonEnhancement>
                                                                <MenuButton
                                                                    appearance="subtle"
                                                                    size="small"
                                                                    icon={<FilterRegular />}
                                                                    title={t('adminSettings.rbac.filterRole', 'Filter Role')}
                                                                />
                                                            </MenuTrigger>
                                                            <MenuPopover>
                                                                <div style={{ padding: '8px' }}>
                                                                    <Input
                                                                        placeholder={t('adminSettings.rbac.filterRolePlaceholder', 'Filter by Role...')}
                                                                        value={filters.role}
                                                                        onChange={(e) => handleFilterChange('role', e.target.value)}
                                                                        size="small"
                                                                    />
                                                                </div>
                                                            </MenuPopover>
                                                        </Menu>
                                                    </div>
                                                    {sortConfig?.key === 'role' && (
                                                        <Text size={200} style={{ color: '#0078d4' }}>
                                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                        </Text>
                                                    )}
                                                </div>
                                            </TableHeaderCell>
                                            <TableHeaderCell style={{ fontWeight: 'bold' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                    <Text>{t('adminSettings.rbac.actions', 'Actions')}</Text>
                                                    <Tooltip content={t('adminSettings.rbac.addNewUser', 'Add new user/role assignment')} relationship="label">
                                                        <Button
                                                            appearance="subtle"
                                                            size="small"
                                                            icon={<AddRegular />}
                                                            onClick={handleAddNew}
                                                            disabled={editingRowIndex !== null}
                                                            title={t('adminSettings.rbac.addNewUser', 'Add new user/role assignment')}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredAndSortedData.length > 0 ? (
                                            filteredAndSortedData.map((user, index) => {
                                                // Find the original index in usersData for editing purposes
                                                const originalIndex = usersData.findIndex(originalUser => 
                                                    originalUser.userId === user.userId && originalUser.role === user.role
                                                );
                                                
                                                return (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            {editingRowIndex === originalIndex ? (
                                                                <Input
                                                                    value={editingData.userId}
                                                                    onChange={(e) => handleInputChange('userId', e.target.value)}
                                                                    size="small"
                                                                    placeholder={t('adminSettings.rbac.userIdPlaceholder', 'Enter user ID')}
                                                                />
                                                            ) : (
                                                                user.userId
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {editingRowIndex === originalIndex ? (
                                                                <Dropdown
                                                                    placeholder={t('adminSettings.rbac.rolePlaceholder', 'Select role')}
                                                                    value={editingData.role}
                                                                    selectedOptions={editingData.role ? [editingData.role] : []}
                                                                    onOptionSelect={(_, data) => {
                                                                        if (data.optionValue) {
                                                                            handleInputChange('role', data.optionValue);
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    {availableRoles.slice().sort().map((role) => (
                                                                      <Option key={role} value={role}>{role}</Option>
                                                                    ))}
                                                                </Dropdown>
                                                            ) : (
                                                                user.role
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                {editingRowIndex === originalIndex ? (
                                                                    <>
                                                                        <Tooltip content={t('adminSettings.rbac.save', 'Save changes')} relationship="label">
                                                                            <Button
                                                                                appearance="subtle"
                                                                                size="small"
                                                                                icon={<CheckmarkRegular />}
                                                                                onClick={handleSave}
                                                                                title={t('adminSettings.rbac.save', 'Save changes')}
                                                                            />
                                                                        </Tooltip>
                                                                        <Tooltip content={t('adminSettings.rbac.cancel', 'Cancel changes')} relationship="label">
                                                                            <Button
                                                                                appearance="subtle"
                                                                                size="small"
                                                                                icon={<DismissRegular />}
                                                                                onClick={handleCancel}
                                                                                title={t('adminSettings.rbac.cancel', 'Cancel changes')}
                                                                            />
                                                                        </Tooltip>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Tooltip content={t('adminSettings.rbac.delete', 'Delete user/role assignment')} relationship="label">
                                                                            <Button
                                                                                appearance="subtle"
                                                                                size="small"
                                                                                icon={<DeleteRegular />}
                                                                                onClick={() => handleDelete(user)}
                                                                                disabled={editingRowIndex !== null}
                                                                                title={t('adminSettings.rbac.delete', 'Delete user/role assignment')}
                                                                            />
                                                                        </Tooltip>
                                                                        <Tooltip content={t('adminSettings.rbac.edit', 'Edit user/role assignment')} relationship="label">
                                                                            <Button
                                                                                appearance="subtle"
                                                                                size="small"
                                                                                icon={<EditRegular />}
                                                                                onClick={() => handleEdit(user)}
                                                                                disabled={editingRowIndex !== null}
                                                                                title={t('adminSettings.rbac.edit', 'Edit user/role assignment')}
                                                                            />
                                                                        </Tooltip>
    
                                                                        {/* button to show role configuration */}
                                                                        <Tooltip content={t('adminSettings.rbac.showRoleConfig', 'Show role configuration')} relationship="label">
                                                                            <Button
                                                                                appearance="subtle"
                                                                                size="small"
                                                                                icon={<Settings48Regular />}
                                                                                onClick={() => handleShowRoleConfig(user.role)}
                                                                                disabled={editingRowIndex !== null}
                                                                                title={t('adminSettings.rbac.showRoleConfig', 'Show role configuration')}
                                                                            />
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3}>
                                                    {t('adminSettings.rbac.noData', 'No data available')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
    
    
                                {/* Role configuration section on the right */}
                                {showRoleConfig && (
                                    <div style={{
                                        position: 'fixed',
                                        right: 0,
                                        top: 0,
                                        height: '100vh',
                                        minHeight: 0,
                                        width: isMobile ? '100%' : '400px',
                                        background: '#f5f5f5',
                                        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
                                        zIndex: 100,
                                        padding: isMobile ? '16px 8px' : '24px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}>
                                        <div style={{ position: 'relative', marginBottom: 16, width: '100%' }}>
                                            <Text
                                                size={500}
                                                style={{
                                                    fontWeight: 'bold',
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    textAlign: 'center',
                                                    paddingRight: isMobile ? 32 : 0,
                                                }}
                                            >
                                                {t('adminSettings.rbac.roleConfigTitle', 'Role')}: {selectedRoleForConfig}
                                            </Text>
    
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<DismissRegular />}
                                                onClick={handleCloseRoleConfig}
                                                title={t('adminSettings.rbac.closeRoleConfig', 'Close')}
                                                style={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
                                            />
                                        </div>
    
                                        {roleConfigLoading ? (
                                            <Text>{t('adminSettings.rbac.roleConfigLoading', 'Loading configuration...')}</Text>
                                        ) : roleConfigError ? (
                                            <Text style={{ color: 'red' }}>{roleConfigError}</Text>
                                        ) : (
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        flex: 1,
                                                        minHeight: 0,
                                                        fontFamily: 'monospace',
                                                        overflowY: 'auto',
                                                        background: '#fff',
                                                        color: '#222',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd',
                                                        padding: 8,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {roleConfigData}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}                    
    
                                </>
                            )}
                        </div>
    
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagementView;
